import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePager } from "../components/Pagination";
import PageHeader from "../components/PageHeader";
import { Plus, CheckCircle2, FileText, Truck, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";
import DeleteRequestButton from "../components/DeleteRequestButton";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_supply-control-20/artifacts/rvxe15j7_1.png";

const TODAY = () => new Date().toISOString().slice(0, 10);
const EMPTY = () => ({
  type: "non_returnable", supplier_id: "", party_name: "", party_address: "", party_gst: "",
  items: [{ material_id: "", description: "", qty: 1, rate: 0, uom: "PCS" }],
  vehicle_no: "", driver_name: "", transporter: "",
  authorised_signatory: "", expected_return_date: "", remarks: "",
});

export default function DeliveryChallans() {
  // Feb-2026 — submit guard prevents double-tap duplicates on tablets.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [grns, setGrns] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [company, setCompany] = useState({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);  // when resubmitting a sent-back DC
  const [view, setView] = useState(null);
  const [form, setForm] = useState(EMPTY());
  const [invoiceFor, setInvoiceFor] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [reviewOf, setReviewOf] = useState(null); // {id, number, remark}
  const [q, setQ] = useState("");
  const { user } = useAuth();

  const load = () => Promise.all([
    api.get("/challans"),
    api.get("/suppliers"),
    api.get("/company-profile"),
    api.get("/grns"),
    api.get("/materials"),
  ]).then(([c, s, cp, g, m]) => {
    setRows(c.data); setParties((s.data || []).filter((x) => x.active !== false));
    setCompany(cp.data || {});
    setGrns((g.data || []).filter((x) => x.status === "stock_in" || x.status === "approved"));
    setMaterials(m.data || []);
  });
  useEffect(() => { load(); }, []);

  const total = () => (form.items || []).reduce((a, x) => a + (+x.qty || 0) * (+x.rate || 0), 0);
  const formTotal = total();

  const updateItem = (i, patch) => setForm((f) => ({
    ...f,
    items: f.items.map((x, idx) => {
      if (idx !== i) return x;
      const merged = { ...x, ...patch };
      // When material changes, auto-fill description, uom, rate from master
      if ("material_id" in patch) {
        const m = materials.find((mm) => mm.id === patch.material_id);
        if (m) {
          merged.description = `${m.code} — ${m.name}`;
          merged.uom = m.uom || "PCS";
          merged.rate = m.cost ?? 0;
        }
      }
      return merged;
    }),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { material_id: "", description: "", qty: 1, rate: 0, uom: "PCS" }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const onPartyChange = (pid) => {
    const p = parties.find((x) => x.id === pid);
    if (!p) { setForm({ ...form, supplier_id: "", party_name: "", party_address: "", party_gst: "" }); return; }
    setForm({ ...form, supplier_id: pid, party_name: p.name, party_address: p.address || "", party_gst: p.gst_number || "" });
  };

  const onSourceGrnChange = (gid) => {
    const g = grns.find((x) => x.id === gid);
    setForm({ ...form, source_grn_id: gid || "", source_grn_number: g?.number || "" });
  };

  const save = async () => {
    if (!form.party_name) { toast.error("Party required"); return; }
    if (form.type === "returnable" && !form.expected_return_date) { toast.error("Expected return date required for returnable"); return; }
    if (form.items.some((i) => !i.material_id)) { toast.error("Each row needs a material selected from master"); return; }
    if (form.items.some((i) => !i.qty || +i.qty <= 0)) { toast.error("Qty must be > 0 on every row"); return; }
    try {
      const payload = { ...form, items: form.items.map((i) => ({ ...i, qty: +i.qty, rate: +i.rate })) };
      await submitGuard(async (idemKey) => {
        if (editingId) {
          await api.post(`/challans/${editingId}/resubmit`, payload, { headers: { "Idempotency-Key": idemKey } });
          toast.success("Resubmitted — pending approval");
        } else {
          await api.post("/challans", payload, { headers: { "Idempotency-Key": idemKey } });
          toast.success("Challan created — pending approval");
        }
      });
      setOpen(false); setEditingId(null); setForm(EMPTY()); resetGuard(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editSentBack = (c) => {
    setForm({
      type: c.type, supplier_id: c.supplier_id || "",
      party_name: c.party_name, party_address: c.party_address || "", party_gst: c.party_gst || "",
      items: (c.items || []).map((it) => {
        const m = materials.find((mm) => `${mm.code} — ${mm.name}` === it.description || mm.code === (it.description || "").split(" ")[0]);
        return { material_id: it.material_id || m?.id || "", description: it.description, qty: it.qty, rate: it.rate, uom: it.uom || "PCS" };
      }),
      vehicle_no: c.vehicle_no || "", driver_name: c.driver_name || "", transporter: c.transporter || "",
      authorised_signatory: c.authorised_signatory || "", expected_return_date: c.expected_return_date || "",
      remarks: c.remarks || "",
      source_grn_id: c.source_grn_id || "", source_grn_number: c.source_grn_number || "",
    });
    setEditingId(c.id); setOpen(true);
  };

  const approve = async (c) => {
    try { await api.post(`/challans/${c.id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decideDC = async (decision) => {
    if (!reviewOf) return;
    if (decision === "send_back" && !(reviewOf.remark || "").trim()) { toast.error("Reason required for Send Back"); return; }
    if (decision === "reject" && !(reviewOf.remark || "").trim()) { toast.error("Reason required for Reject"); return; }
    try {
      if (decision === "approve") await api.post(`/challans/${reviewOf.id}/approve`);
      else if (decision === "send_back") await api.post(`/challans/${reviewOf.id}/send-back`, { remark: reviewOf.remark });
      else if (decision === "reject") await api.post(`/challans/${reviewOf.id}/reject`, { remark: reviewOf.remark });
      toast.success(decision === "approve" ? "Approved" : decision === "send_back" ? "Sent back" : "Rejected");
      setReviewOf(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const overdueDays = (c) => {
    try { const d = new Date(c.created_at); return Math.floor((Date.now() - d.getTime()) / 86400000); } catch { return 0; }
  };

  const renderStatus = (c) => {
    let label = c.status, color = "#0D2B5E";
    const days = overdueDays(c);
    if (c.status === "closed") { label = "CLOSED"; color = "#2E7D32"; }
    else if (c.status === "partially_returned") { label = "PARTIALLY CLOSED"; color = "#1565C0"; }
    else if (c.type === "non_returnable" && c.status === "invoice_pending") {
      if (days >= 7) { label = "BLOCKED (>7d)"; color = "#C62828"; }
      else if (days >= 5) { label = "INV ALERT (>5d)"; color = "#E65100"; }
      else label = "INVOICE PENDING";
    }
    if (c.type === "returnable" && c.status !== "closed") {
      const ed = c.expected_return_date ? new Date(c.expected_return_date) : null;
      if (ed) {
        const od = Math.floor((Date.now() - ed.getTime()) / 86400000);
        if (od > 15) { label = "ESCALATED (>45d)"; color = "#C62828"; }
        else if (od > 0) { label = "EXTENSION (>30d)"; color = "#E65100"; }
      }
    }
    return <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>{label.toUpperCase()}</span>;
  };

  const refClosure = (c) => {
    if (c.type === "non_returnable" && c.invoice_uploaded) return c.invoice_no || "INV ✓";
    if (c.type === "returnable" && c.inward_grn_number) return c.inward_grn_number;
    return "—";
  };

  const filtered = rows.filter((c) => {
    if (!q) return true;
    const k = q.toLowerCase();
    return (c.number || "").toLowerCase().includes(k) || (c.party_name || "").toLowerCase().includes(k) || (c.source_grn_number || "").toLowerCase().includes(k);
  });
  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(filtered, 10);

  return (
    <div data-testid="challans-page">
      <PageHeader title="Delivery Challans" subtitle="Single common format · Returnable + Non-Returnable"
        helpText="Approval thresholds: ≤ ₹50k by Plant Head, > ₹50k by Super Admin only. Non-returnable: Day-5 alert, Day-7 BLOCKS new challans for the SAME PARTY until invoice uploaded. Returnable: 30-day deadline, 15-day extension by PH, ≥45d → SA escalation. Return DCs link to the original GRN for full traceability."
        right={<button onClick={() => { setForm(EMPTY()); setEditingId(null); setOpen(true); }} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-challan-btn"><Plus size={14}/> New Challan</button>}
      />
      <div className="p-8">
        <div className="mb-3 relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="challan-search" placeholder="Search number, party, source GRN…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "9%" }}/><col style={{ width: "9%" }}/><col style={{ width: "15%" }}/>
              <col style={{ width: "9%" }}/><col style={{ width: "13%" }}/><col style={{ width: "11%" }}/><col style={{ width: "9%" }}/>
              <col style={{ width: "10%" }}/><col style={{ width: "15%" }}/>
            </colgroup>
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-3 py-2.5">Number</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Party</th>
                <th className="text-right px-3 py-2.5">Total ₹</th>
                <th className="text-right px-3 py-2.5" title="Dispatched · Returned · Balance">Qty · Bal</th>
                <th className="text-left px-3 py-2.5">Closure Ref</th>
                <th className="text-left px-3 py-2.5">Approver</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((c, i) => {
                const dispatched = (c.items || []).reduce((a, x) => a + (+x.qty || 0), 0);
                const returned = +c.returned_qty || 0;
                const balance = Math.max(0, dispatched - returned);
                return (
                <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-3 py-2.5 font-mono text-[#1565C0] truncate"><button onClick={() => setView(c)} className="hover:underline font-bold text-left" data-testid={`dc-link-${c.number}`}>{c.number}</button></td>
                  <td className="px-3 py-2.5"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${c.type === "returnable" ? "bg-[#1565C0]/10 text-[#1565C0]" : "bg-[#0D2B5E]/10 text-[#0D2B5E]"}`}>{c.type === "returnable" ? "RETURN" : "NON-RET"}</span></td>
                  <td className="px-3 py-2.5 truncate" title={c.party_name}>{c.party_name}</td>
                  <td className="px-3 py-2.5 font-mono text-right">₹{c.total_value?.toFixed(2)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[11px]" data-testid={`dc-qty-bal-${c.number}`}>
                    {c.type === "returnable" ? (
                      <>
                        <span className="text-[#0D2B5E]/70">{dispatched}</span>
                        <span className="text-[#0D2B5E]/30 mx-0.5">·</span>
                        <span className="text-[#2E7D32]">{returned}</span>
                        <span className="text-[#0D2B5E]/30 mx-0.5">·</span>
                        <span className={`font-bold ${balance === 0 ? "text-[#2E7D32]" : "text-[#E65100]"}`} title={`Balance (to be returned): ${balance}`}>{balance}</span>
                      </>
                    ) : (
                      <span className="text-[#0D2B5E]/70">{dispatched}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs text-[#1565C0] truncate" title={refClosure(c)}>{refClosure(c)}</td>
                  <td className="px-3 py-2.5 text-xs truncate">{c.approved_by || "—"}</td>
                  <td className="px-3 py-2.5">{renderStatus(c)}</td>
                  <td className="px-3 py-2.5 text-right space-x-1">
                    {c.status === "pending_approval" && (user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin") && (
                      <button onClick={() => setReviewOf({ id: c.id, number: c.number, total: c.total_value, remark: "" })} className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`review-challan-${c.number}`}><CheckCircle2 size={12}/>Review</button>
                    )}
                    {c.status === "invoice_pending" && (
                      <button onClick={() => setInvoiceFor(c)} className="text-xs bg-[#E65100] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`invoice-${c.number}`}><FileText size={12}/>Invoice</button>
                    )}
                    {c.type === "returnable" && ["issued", "partially_returned"].includes(c.status) && (
                      <button onClick={() => setReturnFor(c)} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`return-${c.number}`}><RotateCcw size={12}/>Return</button>
                    )}
                    {c.status === "sent_back" && (c.created_by === user?.id || user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin") && (
                      <button onClick={() => editSentBack(c)} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`resubmit-challan-${c.number}`}>Edit & Resubmit</button>
                    )}
                    {!["pending_approval", "rejected", "sent_back"].includes(c.status) && (
                      <button onClick={() => { setView(c); setTimeout(() => document.querySelector('[data-testid=print-challan-doc]')?.click(), 400); }} className="text-xs border border-[#1565C0] text-[#1565C0] px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`print-challan-${c.number}`}>Print</button>
                    )}
                    {c.status === "sent_back" && c.send_back_remark && (
                      <span className="block text-[9px] uppercase tracking-widest text-[#E65100] mt-1" title={c.send_back_remark} data-testid={`sendback-reason-${c.number}`}>↪ {c.send_back_remark.slice(0, 30)}{c.send_back_remark.length > 30 ? "…" : ""}</span>
                    )}
                    <span className="inline-block align-middle"><DeleteRequestButton module="challan" record={c} label={c.number} onDeleted={load}/></span>
                  </td>
                </tr>
              );})}
              {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-[#0D2B5E]/50">No challans.</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>
      </div>

      {open && (
        <Dialog title={editingId ? "Edit & Resubmit Challan" : "New Delivery Challan"} onClose={() => { setOpen(false); setEditingId(null); }} wide>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Type *</label>
              <div className="grid grid-cols-2 gap-2">
                {[["non_returnable", "Non-Returnable"], ["returnable", "Returnable"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, type: v })} data-testid={`challan-type-${v}`}
                    className={`px-3 py-2 text-xs rounded-sm uppercase tracking-widest font-bold border ${form.type === v ? "bg-[#1565C0] text-white border-[#1565C0]" : "bg-white text-[#0D2B5E] border-[#EEEEEE]"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Party / Consignee *</label>
              <select value={form.supplier_id} onChange={(e) => onPartyChange(e.target.value)} data-testid="challan-party-dropdown" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">Select party…</option>
                {parties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ""}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Party Address (auto-filled)</label>
              <input value={form.party_address || ""} onChange={(e) => setForm({ ...form, party_address: e.target.value })} data-testid="challan-party-address" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm bg-[#F5F5F5]/40"/>
            </div>
            <Input label="Party GST (auto-filled)" value={form.party_gst} onChange={(v) => setForm({ ...form, party_gst: v })}/>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Source GRN (Return DC linkage)</label>
              <select value={form.source_grn_id} onChange={(e) => onSourceGrnChange(e.target.value)} data-testid="challan-source-grn" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm">
                <option value="">— None / Outward DC —</option>
                {grns.map((g) => <option key={g.id} value={g.id}>{g.number} · {g.supplier_id?.slice(0, 6)}…</option>)}
              </select>
              {form.source_grn_id && <div className="text-[10px] text-[#2E7D32] uppercase tracking-widest mt-1">Linked to original GRN — full traceability</div>}
            </div>
            <Input label="Vehicle No." value={form.vehicle_no} onChange={(v) => setForm({ ...form, vehicle_no: v })}/>
            <Input label="Driver Name" value={form.driver_name} onChange={(v) => setForm({ ...form, driver_name: v })}/>
            <Input label="Transporter" value={form.transporter} onChange={(v) => setForm({ ...form, transporter: v })}/>
            <Input label="Authorised Signatory" value={form.authorised_signatory} onChange={(v) => setForm({ ...form, authorised_signatory: v })}/>
            {form.type === "returnable" && (
              <Input label="Expected Return Date *" type="date" value={form.expected_return_date} onChange={(v) => setForm({ ...form, expected_return_date: v })} testId="challan-return-date"/>
            )}
            <Input label="Remarks" value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })}/>
          </div>
          <div className="mb-2 flex justify-between items-center">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Items</div>
            <button onClick={addItem} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="challan-add-item">+ Add item</button>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr><th className="text-left px-2 py-1">Description</th><th className="text-right px-2 py-1">Qty</th><th className="text-left px-2 py-1">UOM</th><th className="text-right px-2 py-1">Rate ₹</th><th className="text-right px-2 py-1">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const supId = form.supplier_id;
                const filtered = supId ? materials.filter((m) => (m.supplier_ids || []).includes(supId)) : materials;
                return (
                  <tr key={i}>
                    <td className="p-1">
                      <select value={it.material_id || ""} onChange={(e) => updateItem(i, { material_id: e.target.value })} data-testid={`challan-mat-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm">
                        <option value="">{filtered.length ? "Select material…" : "No materials available"}</option>
                        {filtered.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
                        {it.material_id && !filtered.some((m) => m.id === it.material_id) && (
                          <option value={it.material_id}>{it.description}</option>
                        )}
                      </select>
                    </td>
                    <td className="p-1"><input type="number" step="any" min="0" value={it.qty} onChange={(e) => updateItem(i, { qty: e.target.value })} data-testid={`challan-qty-${i}`} className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right"/></td>
                    <td className="p-1"><input value={it.uom} disabled className="w-16 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono bg-[#F5F5F5] text-[#0D2B5E]/70"/></td>
                    <td className="p-1"><input type="number" min="0" step="0.01" value={it.rate} disabled data-testid={`challan-rate-${i}`} className="w-24 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right bg-[#F5F5F5] text-[#0D2B5E]/70"/></td>
                    <td className="p-1 font-mono text-right text-xs">₹{((+it.qty || 0) * (+it.rate || 0)).toFixed(2)}</td>
                    <td className="p-1">{form.items.length > 1 && <button onClick={() => removeItem(i)} className="text-[#C62828] text-xs">×</button>}</td>
                  </tr>
                );
              })}
              <tr className="bg-[#F5F5F5]"><td colSpan={4} className="px-2 py-2 text-right font-bold uppercase tracking-widest text-xs text-[#0D2B5E]">Total</td><td className="px-2 py-2 font-mono text-right font-bold text-[#1565C0]" data-testid="challan-total">₹{formTotal.toFixed(2)}</td><td></td></tr>
            </tbody>
          </table>
          <div className="text-xs text-[#E65100] mt-2 flex items-center gap-2">
            <Truck size={14}/> Approval: {formTotal > 50000 ? <strong>Super Admin only (&gt; ₹50k)</strong> : <strong>Plant Head OR Super Admin (≤ ₹50k)</strong>}
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-challan-btn">{submitBusy ? "Submitting…" : "Submit"}</button>
          </div>
        </Dialog>
      )}

      {invoiceFor && (
        <InvoiceUploadModal challan={invoiceFor} onClose={() => setInvoiceFor(null)} onDone={() => { setInvoiceFor(null); load(); }}/>
      )}
      {returnFor && (
        <ReturnModal challan={returnFor} onClose={() => setReturnFor(null)} onDone={() => { setReturnFor(null); load(); }}/>
      )}
      {reviewOf && (
        <Dialog title={`Review ${reviewOf.number}`} onClose={() => setReviewOf(null)}>
          <div className="mb-3 text-xs text-[#0D2B5E]/70">
            Total: <span className="font-mono text-[#1565C0] font-bold">₹{(reviewOf.total || 0).toFixed(2)}</span>
            {(reviewOf.total || 0) > 50000 && <span className="ml-2 text-[#E65100] uppercase tracking-widest font-bold text-[10px]">SA approval required (&gt; ₹50k)</span>}
          </div>
          <textarea placeholder="Remark (required for Send Back / Reject)" value={reviewOf.remark || ""} onChange={(e) => setReviewOf({ ...reviewOf, remark: e.target.value })} rows={3} data-testid="dc-review-remark" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => decideDC("reject")} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="dc-reject-btn">Reject</button>
            <button onClick={() => decideDC("send_back")} className="px-4 py-2 bg-[#E65100] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="dc-sendback-btn">Send Back</button>
            <button onClick={() => decideDC("approve")} className="px-4 py-2 bg-[#2E7D32] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="dc-approve-btn">Approve</button>
          </div>
        </Dialog>
      )}
      {view && <ChallanView challan={view} company={company} onClose={() => setView(null)}/>}
    </div>
  );
}

function InvoiceUploadModal({ challan, onClose, onDone }) {
  const [invNo, setInvNo] = useState("");
  const submit = async () => {
    if (!invNo) { toast.error("Invoice number required"); return; }
    try { await api.post(`/challans/${challan.id}/invoice-upload`, { invoice_no: invNo }); toast.success("Invoice recorded — challan closed"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog title={`Invoice for ${challan.number}`} onClose={onClose}>
      <Input label="Invoice Number" value={invNo} onChange={setInvNo} testId="invoice-no"/>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
        <button onClick={submit} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="save-invoice-btn">Save & Close Challan</button>
      </div>
    </Dialog>
  );
}

function ReturnModal({ challan, onClose, onDone }) {
  const [qty, setQty] = useState(1);
  const [ref, setRef] = useState("");
  const submit = async () => {
    if (!ref) { toast.error("Return ref required"); return; }
    try { await api.post(`/challans/${challan.id}/record-return`, { returned_qty: +qty, return_ref: ref }); toast.success("Return recorded"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog title={`Record Return for ${challan.number}`} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Returned Qty" type="number" step="any" value={qty} onChange={setQty} testId="ret-qty"/>
        <Input label="Return Ref Number" value={ref} onChange={setRef} testId="ret-ref"/>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
        <button onClick={submit} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="save-return-btn">Record Return</button>
      </div>
    </Dialog>
  );
}

function ChallanView({ challan, company, onClose }) {
  const [copies, setCopies] = useState(2);
  const total = (challan.items || []).reduce((a, x) => a + (x.qty * x.rate), 0);
  const isVoid = challan.status === "rejected";
  const voidLabel = "REJECTED";
  const renderCopy = (copyLabel, key) => (
    <div key={key} className="dc-copy relative" data-testid={`dc-copy-${key}`}>
      {isVoid && (
        <div className="void-watermark pointer-events-none absolute inset-0 z-10 flex items-center justify-center" data-testid="dc-void-watermark"
          style={{ transform: "rotate(-22deg)", opacity: 0.18 }}>
          <div style={{ fontSize: "9rem", fontWeight: 900, color: "#C62828", letterSpacing: "0.2em", border: "8px solid #C62828", padding: "1rem 3rem", whiteSpace: "nowrap" }}>
            {voidLabel}
          </div>
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#0D2B5E]/60 mb-1">{copyLabel}</div>
      {/* Company Header (Consignor) */}
      <div className="flex items-start justify-between border-b-4 border-[#1565C0] pb-2 mb-3 gap-4">
        <div className="flex items-start gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-12 object-contain mt-1"/>
          <div>
            <div className="text-base font-bold text-[#0D2B5E]" data-testid="dc-company-name">{company?.name || "Supplar Engineering Pvt Ltd"}</div>
            <div className="text-[11px] text-[#0D2B5E]/70 max-w-md">{company?.address}</div>
            <div className="text-[10px] text-[#0D2B5E]/70 mt-0.5 font-mono">
              {company?.gst_number && <>GSTIN: <span className="text-[#0D2B5E]">{company.gst_number}</span> · </>}
              {company?.phone}{company?.email && ` · ${company.email}`}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className={`inline-block text-xs font-bold uppercase tracking-widest px-2 py-0.5 ${challan.type === "returnable" ? "bg-[#1565C0] text-white" : "bg-[#0D2B5E] text-white"}`}>
            {challan.type === "returnable" ? "RETURNABLE CHALLAN" : "NON-RETURNABLE CHALLAN"}
          </div>
          <div className="text-[11px] font-mono mt-1">No. <span className="text-[#1565C0] font-bold">{challan.number}</span></div>
          <div className="text-[11px] font-mono">Date: {challan.created_at?.slice(0, 10)}</div>
          {challan.source_grn_number && <div className="text-[10px] mt-0.5 uppercase tracking-widest text-[#2E7D32]">Return Ref. GRN: {challan.source_grn_number}</div>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-2 text-xs">
        <div className="border-l-2 border-[#1565C0] pl-2">
          <div className="text-[9px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">Consignee</div>
          <div className="font-bold">{challan.party_name}</div>
          <div className="text-[10px] text-[#0D2B5E]/70">{challan.party_address}</div>
          {challan.party_gst && <div className="text-[10px] font-mono">GSTIN: {challan.party_gst}</div>}
        </div>
        <div className="border-l-2 border-[#1565C0] pl-2">
          <div className="text-[9px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">Vehicle / Transport</div>
          <div className="font-mono text-xs">{challan.vehicle_no || "—"}</div>
          <div className="text-[10px]">{challan.driver_name} · {challan.transporter}</div>
          {challan.expected_return_date && <div className="text-[10px] text-[#E65100] mt-0.5">Expected Return: {challan.expected_return_date}</div>}
        </div>
      </div>
      <table className="w-full text-xs border border-[#0D2B5E]">
        <thead className="bg-[#0D2B5E] text-white text-[9px] uppercase tracking-widest">
          <tr><th className="text-left px-2 py-1">Description</th><th className="text-right px-2 py-1">Qty</th><th className="text-left px-2 py-1">UOM</th><th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">Amount</th></tr>
        </thead>
        <tbody>
          {(challan.items || []).map((it, i) => (
            <tr key={i} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
              <td className="px-2 py-1">{it.description}</td>
              <td className="px-2 py-1 font-mono text-right">{fmtQty(it.qty)}</td>
              <td className="px-2 py-1 font-mono">{it.uom}</td>
              <td className="px-2 py-1 font-mono text-right">₹{it.rate?.toFixed(2)}</td>
              <td className="px-2 py-1 font-mono text-right">₹{(it.qty * it.rate).toFixed(2)}</td>
            </tr>
          ))}
          <tr className="bg-[#F5F5F5]"><td colSpan={4} className="px-2 py-1 text-right font-bold uppercase tracking-widest text-[10px]">Grand Total</td><td className="px-2 py-1 font-mono text-right font-bold text-[#1565C0]">₹{total.toFixed(2)}</td></tr>
        </tbody>
      </table>
      {challan.type === "returnable" && (() => {
        const dispatched = (challan.items || []).reduce((a, x) => a + (+x.qty || 0), 0);
        const returned = +challan.returned_qty || 0;
        const balance = Math.max(0, dispatched - returned);
        return (
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[10px]">
            <div className="border-l-2 border-[#1565C0] bg-[#1565C0]/5 p-1.5">
              <div className="tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Dispatched</div>
              <div className="text-base font-bold font-mono text-[#1565C0]">{dispatched}</div>
            </div>
            <div className="border-l-2 border-[#2E7D32] bg-[#2E7D32]/5 p-1.5">
              <div className="tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Returned</div>
              <div className="text-base font-bold font-mono text-[#2E7D32]">{returned}</div>
            </div>
            <div className={`border-l-2 ${balance === 0 ? "border-[#2E7D32] bg-[#2E7D32]/5" : "border-[#E65100] bg-[#E65100]/5"} p-1.5`}>
              <div className="tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Balance</div>
              <div className={`text-base font-bold font-mono ${balance === 0 ? "text-[#2E7D32]" : "text-[#E65100]"}`}>{balance}</div>
            </div>
          </div>
        );
      })()}
      {challan.remarks && <div className="text-[10px] mt-1.5"><strong>Remarks:</strong> {challan.remarks}</div>}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div><div className="text-[10px] text-[#0D2B5E]/60 border-t border-black pt-1">Receiver Signature</div></div>
        <div className="text-right">
          <div className="text-[10px] text-[#0D2B5E]/60 border-t border-black pt-1 inline-block min-w-[180px]">
            <div className="font-bold pt-1 text-xs">{challan.approved_by || challan.authorised_signatory || "—"}</div>
            <div>Plant Head / Managing Director</div>
            {challan.approved_at && <div className="text-[9px] text-[#2E7D32] font-mono uppercase tracking-widest font-bold">APPROVED · {fmtIST(challan.approved_at)}</div>}
            {challan.status === "rejected" && (
              <div className="text-[9px] text-[#C62828] font-mono uppercase tracking-widest font-bold">REJECTED · {fmtIST(challan.rejected_at)} · by {challan.rejected_by || "—"}</div>
            )}
            {challan.status === "sent_back" && (
              <div className="text-[9px] text-[#E65100] font-mono uppercase tracking-widest font-bold">SENT BACK · {fmtIST(challan.sent_back_at)} · by {challan.sent_back_by || "—"}</div>
            )}
            {challan.status === "pending_approval" && (
              <div className="text-[9px] text-[#0D2B5E]/50 font-mono uppercase tracking-widest font-bold">PENDING APPROVAL — NOT VALID</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  const printAreaRef = React.useRef(null);
  const handlePrint = () => {
    const node = printAreaRef.current;
    if (!node) return;
    // Collect all stylesheets so the print window picks up Tailwind classes
    const styles = Array.from(document.styleSheets).map((s) => {
      try {
        return Array.from(s.cssRules).map((r) => r.cssText).join("\n");
      } catch (_e) {
        return s.href ? `@import url("${s.href}");` : "";
      }
    }).join("\n");
    const w = window.open("", "_blank", "width=1024,height=800");
    if (!w) {
      window.print();
      return;
    }
    // SECURITY: intentionally NOT using the legacy dynamic-write sink
    // (which would be an XSS vector). We build the print document via
    // safe DOM APIs — title text is set on the element directly (never
    // parsed as HTML), styles are injected via textContent, and the
    // challan markup is cloned (already sanitized by React render).
    // `challan.number` is therefore never interpolated into raw HTML.
    const doc = w.document;
    doc.open();
    doc.close();   // initialise an empty document
    doc.documentElement.lang = "en";
    doc.title = String(challan.number || "");
    // Inject base style sheets
    const styleEl = doc.createElement("style");
    styleEl.textContent = styles;
    doc.head.appendChild(styleEl);
    // Print-specific styles
    const printStyles = doc.createElement("style");
    printStyles.textContent = `
        @page { size: A4 portrait; margin: 8mm; }
        html, body { margin:0; padding:0; background:#fff; }
        .dc-copy { page-break-inside: avoid; }
        .dc-copy + .dc-copy { margin-top: 6mm; border-top: 1px dashed #999; padding-top: 6mm; }
        .no-print { display: none !important; }
    `;
    doc.head.appendChild(printStyles);
    // Charset meta (text content, no parsing)
    const charsetMeta = doc.createElement("meta");
    charsetMeta.setAttribute("charset", "utf-8");
    doc.head.appendChild(charsetMeta);
    // Clone the React-rendered challan DOM directly (never re-serialised through innerHTML)
    doc.body.appendChild(node.cloneNode(true));
    // wait for fonts/images, then print
    const doPrint = () => {
      try { w.focus(); w.print(); } finally { setTimeout(() => w.close(), 400); }
    };
    if (w.document.readyState === "complete") doPrint();
    else w.addEventListener("load", doPrint);
  };
  return (
    <Dialog title={`Challan ${challan.number}`} onClose={onClose} wide>
      <div className="print-area" ref={printAreaRef}>
        {Array.from({ length: copies }).map((_, idx) => renderCopy(copies > 1 ? (idx === 0 ? "Original — for Receiver" : "Duplicate — for Office") : "Office Copy", idx))}
      </div>
      <div className="flex justify-end items-center gap-2 mt-6 no-print">
        <label className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/70 font-bold mr-1">Copies on A4</label>
        <select value={copies} onChange={(e) => setCopies(parseInt(e.target.value, 10))}
          data-testid="dc-print-copies"
          className="border border-[#EEEEEE] px-2 py-2 text-xs rounded-sm font-mono">
          <option value={1}>1</option>
          <option value={2}>2 (recommended)</option>
        </select>
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Close</button>
        <button onClick={handlePrint} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="print-challan-doc">Print</button>
      </div>
    </Dialog>
  );
}

